// @flow

import type { Order } from '../../../types/order'
import type { PriceLine, PriceLineType } from '../../../types/price-line'
import {
  PRICE_LINE_TYPE_DISCOUNT,
  PRICE_LINE_TYPE_PRODUCT,
  PRICE_LINE_TYPE_TOTAL,
  PRICE_LINE_TYPE_COMBI_DEAL_DISCOUNT
} from '../../../types/price-line'
import type { CartState } from './index'
import type { Price } from '../../../types/price'
import { PAYMENT_LINE_TYPE_IMMEDIATE, PAYMENT_LINE_TYPE_TERM, PaymentLine } from '../../../types/payment-line'
import { buildCartItem } from '../../../types/cart-item'
import type { CartItem } from '../../../types/cart-item'
import type { PaymentLineType } from '../../../types/payment-line'
import type { OrderPurchase } from '../../../types/order-purchase'
import { buildOrderPurchase } from '../../../types/order-purchase'

const totalPriceLineByType = (state: CartState, type: PriceLineType): ?PriceLine => {
  const priceSpecification = state.priceSpecification
  if (!priceSpecification || !priceSpecification.totalsPerPriceLineType) {
    return null
  }

  return priceSpecification.totalsPerPriceLineType[type]
}
const totalPriceLineByPaymentType = (state: CartState, type: PaymentLineType): ?PriceLine => {
  const paymentSpecification = state.paymentSpecification
  if (!paymentSpecification || !paymentSpecification.totalsPerPaymentType) {
    return null
  }

  return paymentSpecification.totalsPerPaymentType[type]
}

const productTotalPriceLine = (state: CartState): ?PriceLine => totalPriceLineByType(state, PRICE_LINE_TYPE_PRODUCT)
const discountTotalPriceLine = (state: CartState): ?PriceLine => totalPriceLineByType(state, PRICE_LINE_TYPE_DISCOUNT)
const combiDealDiscountTotalPriceLine = (state: CartState): ?PriceLine => totalPriceLineByType(state, PRICE_LINE_TYPE_COMBI_DEAL_DISCOUNT)
const totalPriceLine = (state: CartState): ?PriceLine => totalPriceLineByType(state, PRICE_LINE_TYPE_TOTAL)

export default {
  order: (state: CartState): Order => {
    return state.order
  },
  orderPurchase: (state: CartState): OrderPurchase => {
    return buildOrderPurchase(state.order)
  },
  cartProductItems: (state: CartState): CartItem[] => {
    const order: Order = state.order
    const items = []
    Object.keys(order.items).forEach(key => {
      const cartItem = buildCartItem(key, order.items[key])
      const priceSpecification = state.priceSpecification
      const paymentSpecification = state.paymentSpecification
      if (priceSpecification) {
        const priceItemSpecifications = priceSpecification.itemSpecifications[key]
        if (priceItemSpecifications) {
          const productPriceLine = priceItemSpecifications.priceLines.find((priceLine: PriceLine) => priceLine.type === PRICE_LINE_TYPE_PRODUCT)
          const discountPriceLine = priceItemSpecifications.priceLines.find((priceLine: PriceLine) => priceLine.type === PRICE_LINE_TYPE_DISCOUNT)
          const combiDealDiscountPriceLine = priceItemSpecifications.priceLines.find((priceLine: PriceLine) => priceLine.type === PRICE_LINE_TYPE_COMBI_DEAL_DISCOUNT)
          const totalPriceLine = priceItemSpecifications.totalPriceLine
          if (productPriceLine) {
            cartItem.amountWithoutVat = productPriceLine.amountWithoutVat
            cartItem.amountWithVat = productPriceLine.amountWithVat
            cartItem.vatPercentage = productPriceLine.vatDescription
            cartItem.vatAmount = productPriceLine.vatAmount
          }
          if (discountPriceLine) {
            cartItem.discountAmount = discountPriceLine.amountWithVat
          }
          if (combiDealDiscountPriceLine) {
            cartItem.combiDealDiscountAmount = combiDealDiscountPriceLine.amountWithVat
          }
          if (totalPriceLine) {
            cartItem.totalAmountWithVat = totalPriceLine.amountWithVat
          }
        }
      }
      if (paymentSpecification && paymentSpecification.itemConfigurations[key]) {
        const paymentItemSpecifications = paymentSpecification.itemConfigurations[key]
        const directPayment = paymentItemSpecifications.payments.find(
          (paymentLine: PaymentLine) => paymentLine.type === PAYMENT_LINE_TYPE_IMMEDIATE
        )
        const termPayments = paymentItemSpecifications.payments.filter(
          (paymentLine: PaymentLine) => paymentLine.type === PAYMENT_LINE_TYPE_TERM
        )
        cartItem.directPayment = directPayment
        cartItem.termPayments = termPayments
      }
      items.push(cartItem)
    })

    return items
  },
  orderDiscountCode: (state: CartState): ?string => {
    return state.order.discountCode
  },
  totalDiscountAmount: (state: CartState): ?Price => {
    const discountTotalLine = discountTotalPriceLine(state)

    return discountTotalLine ? discountTotalLine.amountWithoutVat : undefined
  },
  totalCombiDealDiscountAmount: (state: CartState): ?Price => {
    const combiDealDiscountTotalLine = combiDealDiscountTotalPriceLine(state)

    return combiDealDiscountTotalLine ? combiDealDiscountTotalLine.amountWithoutVat : undefined
  },
  totalAmountWithVat: (state: CartState): ?Price => {
    const totalLine = totalPriceLine(state)

    return totalLine ? totalLine.amountWithVat : undefined
  },
  totalVatAmount: (state: CartState): ?Price => {
    const totalLine = totalPriceLine(state)

    return totalLine ? totalLine.vatAmount : undefined
  },
  totalProductAmountWithVat: (state: CartState): ?Price => {
    const totalTotalsLine = productTotalPriceLine(state)

    return totalTotalsLine ? totalTotalsLine.amountWithVat : undefined
  },
  totalProductAmountWithoutVat: (state: CartState): ?Price => {
    const totalTotalsLine = productTotalPriceLine(state)

    return totalTotalsLine ? totalTotalsLine.amountWithoutVat : undefined
  },
  totalProductVatAmount: (state: Object): ?Price => {
    const totalTotalsLine = productTotalPriceLine(state)

    return totalTotalsLine ? totalTotalsLine.vatAmount : undefined
  },
  directTotalPaymentAmount: (state: CartState): ?Price => {
    const totalImmediatePayment = totalPriceLineByPaymentType(state, PAYMENT_LINE_TYPE_IMMEDIATE)

    return totalImmediatePayment ? totalImmediatePayment.amountWithVat : undefined
  },
  payInTerms: (state: CartState) => {
    return state.payInTerms
  },
  customerDetailsChanged: (state: CartState) => {
    return state.customerDetailsChanged
  },
  overviewRoute: (state: CartState) => {
    return state.shop ? state.shop.overviewRoute : 'ProductOverview'
  },
  paymentRoute: (state: CartState) => {
    return state.shop ? state.shop.paymentRoute : 'Payment'
  },
  orderCustomerNumber: (state: CartState) => {
    if (state.order && state.order.customerDetails && state.order.customerDetails.customerNumber) {
      return state.order.customerDetails.customerNumber.number
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/cart/getters.js